/**
 * Supabase Client Configuration
 * TMC Studio - Cloud backend integration
 */

/// <reference types="vite/client" />

import { logger } from './logger';
import { createClient, SupabaseClient, type User as SupabaseAuthUser } from '@supabase/supabase-js';
import type { BoardDocument, BallDefaults, TextDefaults, EquipmentDefaults } from '@tmc/core';
// DEV-ONLY: see ./devCloud.ts for details. Safe to remove together with
// devCloud.ts and the isDevCloudActive() guards below.
import { isDevCloudActive } from './devCloud';
import * as devCloud from './devCloud';
import { buildLocalizedAuthUrl, getAuthEmailLocale } from '../auth/authEmailLocale';

// Environment variables (Vite)
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
const shouldDetectSessionInUrl =
  typeof window !== 'undefined' && window.location.pathname === '/auth/reset-password';

// Validate environment
if (!supabaseUrl || !supabaseAnonKey) {
  logger.warn(
    '⚠️ Supabase credentials not found. Cloud features will be disabled.\n' +
    'To enable, create apps/web/.env.local with VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY'
  );
}

// Create Supabase client (untyped for flexibility, types will be generated later)
export const supabase: SupabaseClient | null = supabaseUrl && supabaseAnonKey
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        // Google OAuth exchanges PKCE explicitly in AuthCallbackPage so app
        // initialization cannot consume the same one-time code concurrently.
        // Password reset keeps Supabase's built-in URL handling on its own route.
        detectSessionInUrl: shouldDetectSessionInUrl,
        storageKey: 'tmc-auth-token',
        flowType: 'pkce', // PKCE flow — industry standard, more secure than implicit
      },
      global: {
        headers: {
          'X-Client-Info': 'tmc-studio',
        },
      },
    })
  : null;

// Check if Supabase is available
export const isSupabaseEnabled = (): boolean => {
  // DEV-ONLY: treat an active dev mock session as "enabled" so cloud-gated
  // UI (projects, folders, autosave) runs against the devCloud mock.
  return supabase !== null || isDevCloudActive();
};

// =====================================================
// AUTH HELPERS
// =====================================================

export type User = {
  id: string;
  email: string;
  full_name?: string;
  avatar_url?: string;
  subscription_tier: 'free' | 'pro' | 'team';
  stripe_customer_id?: string | null;
  team_id?: string | null;
  /** Club whose Team subscription covers this user as a member, if any. */
  club_organization_id?: string | null;
  preferences?: UserPreferences;
};

export type UserPreferences = {
  theme?: 'light' | 'dark';
  themeMode?: 'light' | 'dark' | 'system';
  gridVisible?: boolean;
  snapEnabled?: boolean;
  cheatSheetVisible?: boolean;
  bottomBar?: { height: number; collapsed?: boolean };
  inspector?: { width: number };
  defaultArrowType?: string;
  stepDuration?: number;
  gridSize?: number;
  squadBenchVisible?: boolean;
  shortcutOverrides?: Record<string, string>;
  coachingProfile?: import('@tmc/core').CoachingProfile;
  arrowDefaults?: {
    strokeWidth?: Record<string, number>;
    color?: Record<string, string>;
    startHead?: string;
    endHead?: string;
  };
  zoneDefaults?: {
    borderStyle?: string;
    borderWidth?: number;
    borderColor?: string;
    showCorners?: boolean;
    fillColor?: string;
    opacity?: number;
  };
  ballDefaults?: BallDefaults;
  textDefaults?: TextDefaults;
  equipmentDefaults?: EquipmentDefaults;
};

const authLocaleSyncs = new Set<string>();

function syncAuthenticatedUserLocale(user: SupabaseAuthUser): void {
  if (!supabase) return;

  const locale = getAuthEmailLocale();
  const syncKey = `${user.id}:${locale}`;
  if (user.user_metadata?.locale === locale || authLocaleSyncs.has(syncKey)) return;

  authLocaleSyncs.add(syncKey);
  void supabase.auth.updateUser({ data: { locale } }).then(({ error }) => {
    if (error) {
      authLocaleSyncs.delete(syncKey);
      logger.warn('[Auth] Could not synchronize email locale:', error.message);
    }
  });
}

/** Get current authenticated user */
export async function getCurrentUser(authUser?: SupabaseAuthUser | null): Promise<User | null> {
  if (!supabase) return null;

  // When the caller already has the authenticated user (e.g. from an
  // onAuthStateChange session), use it directly. This avoids a second
  // /auth/v1/user round trip that supabase-js can abort mid OAuth exchange,
  // which previously left the UI logged-out until a manual refresh.
  let user: SupabaseAuthUser | null = authUser ?? null;
  if (!user) {
    const { data } = await supabase.auth.getUser();
    user = data.user;
  }
  if (!user) return null;

  syncAuthenticatedUserLocale(user);
  
  // ALWAYS fetch fresh profile from database (not from JWT cache)
  // This ensures we get the latest subscription_tier after webhook updates
  const { data, error: fetchError } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', user.id)
    .single();
  
  let profile = data;
  
  // If no profile exists, create one (for OAuth users)
  if (!profile && fetchError) {
    logger.debug('Creating profile for new OAuth user:', user.id);
    const newProfile = {
      id: user.id,
      email: user.email ?? '',
      full_name: user.user_metadata?.full_name ?? user.user_metadata?.name ?? null,
      avatar_url: user.user_metadata?.avatar_url ?? user.user_metadata?.picture ?? null,
      subscription_tier: 'free' as const,
    };
    
    const { data: created, error } = await supabase
      .from('profiles')
      .upsert(newProfile)
      .select()
      .single();
    
    if (error) {
      logger.error('Error creating profile:', error);
      // Return basic user even without profile
      return {
        id: user.id,
        email: user.email ?? '',
        full_name: newProfile.full_name ?? undefined,
        avatar_url: newProfile.avatar_url ?? undefined,
        subscription_tier: 'free',
      };
    }
    
    profile = created;
  }
  
  // Log subscription tier for debugging
  logger.debug(`[getCurrentUser] User ${user.email} - tier: ${profile?.subscription_tier ?? 'free'}`);

  const clubOrganizationId = await getMyClubAccess();

  return {
    id: profile.id,
    email: profile.email,
    full_name: profile.full_name ?? undefined,
    avatar_url: profile.avatar_url ?? undefined,
    subscription_tier: profile.subscription_tier ?? 'free',
    stripe_customer_id: profile.stripe_customer_id ?? null,
    team_id: profile.team_id ?? null,
    club_organization_id: clubOrganizationId,
  };
}

/**
 * Club that grants the current user Team access as a member (the club owner
 * pays for Team). Null when there is none or the check fails: access then
 * falls back to the user's own plan instead of blocking sign-in.
 */
async function getMyClubAccess(): Promise<string | null> {
  if (!supabase) return null;
  try {
    const { data, error } = await supabase.rpc('get_my_club_access');
    if (error) {
      logger.warn('[getCurrentUser] Club access check failed:', error);
      return null;
    }
    return typeof data === 'string' ? data : null;
  } catch (error) {
    logger.warn('[getCurrentUser] Club access check failed:', error);
    return null;
  }
}

/** Sign up with email and password */
export async function signUp(email: string, password: string, fullName?: string) {
  if (!supabase) throw new Error('Supabase not configured');

  const locale = getAuthEmailLocale();
  
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: { full_name: fullName, locale },
      emailRedirectTo: buildLocalizedAuthUrl('/auth/callback'),
    },
  });
  
  if (error) throw error;
  return data;
}

/** Sign in with email and password */
export async function signIn(email: string, password: string) {
  if (!supabase) throw new Error('Supabase not configured');
  
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  
  if (error) throw error;
  return data;
}

/** Sign out */
export async function signOut() {
  if (!supabase) return;
  await supabase.auth.signOut();
}

/** Update user profile */
export async function updateProfile(updates: { full_name?: string; avatar_url?: string }) {
  if (!supabase) throw new Error('Supabase not configured');
  
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  
  const { error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', user.id);
  
  if (error) throw error;
}

/** Get user preferences from database */
export async function getPreferences(): Promise<UserPreferences | null> {
  if (isDevCloudActive()) return null; // DEV-ONLY: no mock preferences yet
  if (!supabase) return null;
  
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  
  const { data, error } = await supabase
    .from('profiles')
    .select('preferences')
    .eq('id', user.id)
    .single();
  
  if (error) {
    logger.error('Error fetching preferences:', error);
    return null;
  }
  
  return (data?.preferences as UserPreferences) ?? null;
}

/** Update user preferences in database */
export async function updatePreferences(preferences: Partial<UserPreferences>): Promise<void> {
  if (isDevCloudActive()) return; // DEV-ONLY: no-op for mock sessions
  if (!supabase) throw new Error('Supabase not configured');
  
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  
  // Get current preferences first
  const currentPrefs = await getPreferences() ?? {};
  
  // Merge with new preferences
  const updatedPrefs = { ...currentPrefs, ...preferences };
  
  const { error } = await supabase
    .from('profiles')
    .update({ preferences: updatedPrefs })
    .eq('id', user.id);
  
  if (error) throw error;
}

/** Atomic JSONB-merge via RPC — used by beforeunload flush to avoid overwriting other keys.
 *  Unlike updatePreferences (read-merge-write), this runs atomically on the DB side. */
export async function mergePreferences(preferences: Partial<UserPreferences>): Promise<void> {
  if (isDevCloudActive()) return;
  if (!supabase) throw new Error('Supabase not configured');

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase.rpc('merge_preferences', {
    p_preferences: preferences,
  });
  if (error) throw error;
}

/** Send reset password email via Supabase Auth.
 *  PKCE code exchange happens on /auth/reset-password route directly.
 *  Supabase SDK detectSessionInUrl handles the exchange automatically. */
export async function resetPasswordForEmail(email: string) {
  if (!supabase) throw new Error('Supabase not configured');

  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: buildLocalizedAuthUrl('/auth/reset-password'),
  });

  if (error) throw error;
  return data;
}

/** Resend confirmation email after signUp. */
export async function resendConfirmationEmail(email: string) {
  if (!supabase) throw new Error('Supabase not configured');

  const { data, error } = await supabase.auth.resend({
    type: 'signup',
    email,
    options: {
      emailRedirectTo: buildLocalizedAuthUrl('/auth/callback'),
    },
  });

  if (error) throw error;
  return data;
}

/** Change password */
export async function changePassword(currentPassword: string, newPassword: string) {
  if (!supabase) throw new Error('Supabase not configured');
  
  const { data: { user } } = await supabase.auth.getUser();
  if (!user?.email) throw new Error('Not authenticated');
  
  // Verify current password by attempting sign in
  const { error: signInError } = await supabase.auth.signInWithPassword({
    email: user.email,
    password: currentPassword,
  });
  
  if (signInError) throw new Error('Current password is incorrect');
  
  // Update to new password
  const { error } = await supabase.auth.updateUser({
    password: newPassword,
  });
  
  if (error) throw error;
}

/**
 * Delete the signed-in user's account and all their data. Runs server-side
 * (netlify/functions/delete-account.ts): Stripe billing, files, then the auth
 * user, which cascades to profile and projects. A user who owns a club with
 * other members gets the error code `ownsClubWithMembers`.
 */
export async function deleteAccount() {
  if (!supabase) throw new Error('Supabase not configured');

  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');

  const response = await fetch('/api/delete-account', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ confirm: 'DELETE' }),
  });
  const result = (await response.json().catch(() => ({}))) as { code?: string; error?: string };
  if (!response.ok) {
    throw Object.assign(new Error(result.error ?? 'Account deletion failed'), { code: result.code ?? 'deletionFailed' });
  }

  // The session belongs to a user that no longer exists.
  await supabase.auth.signOut({ scope: 'local' });
}

/** Sign in with OAuth (Google) */
export async function signInWithGoogle(options?: {
  redirectTo?: string;
  skipBrowserRedirect?: boolean;
}) {
  if (!supabase) throw new Error('Supabase not configured');
  
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      // Keep PKCE exchange on a lightweight callback route, then enter the app.
      redirectTo: options?.redirectTo ?? `${window.location.origin}/auth/callback`,
      skipBrowserRedirect: options?.skipBrowserRedirect,
    },
  });
  
  if (error) throw error;
  return data;
}

// =====================================================
// PROJECTS API
// =====================================================

export interface Project {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  document: BoardDocument;
  thumbnail_url: string | null;
  is_public: boolean;
  is_template: boolean;
  version: number;
  created_at: string;
  updated_at: string;
  folder_id: string | null;
  tags: string[];
  is_favorite: boolean;
  is_pinned: boolean;
  position: number;
}

export interface ProjectInsert {
  name?: string;
  description?: string | null;
  document: BoardDocument;
  thumbnail_url?: string | null;
  is_public?: boolean;
}

export interface ProjectUpdate {
  name?: string;
  description?: string | null;
  document?: BoardDocument;
  thumbnail_url?: string | null;
  is_public?: boolean;
  folder_id?: string | null;
  tags?: string[];
  is_favorite?: boolean;
}

// =====================================================
// FOLDERS TYPES
// =====================================================

export interface ProjectFolder {
  id: string;
  user_id: string;
  name: string;
  color: string;
  icon: string;
  description: string | null;
  parent_id: string | null;
  is_pinned: boolean;
  position: number;
  created_at: string;
  updated_at: string;
}

export interface ProjectFolderInsert {
  name: string;
  color?: string;
  icon?: string;
  description?: string | null;
  parent_id?: string | null;
  position?: number;
}

export interface ProjectFolderUpdate {
  name?: string;
  color?: string;
  icon?: string;
  description?: string | null;
  parent_id?: string | null;
  position?: number;
}

/** Get all projects for current user */
export async function getProjects(): Promise<Project[]> {
  if (isDevCloudActive()) return devCloud.getProjects(); // DEV-ONLY
  if (!supabase) return [];
  
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .order('updated_at', { ascending: false });
  
  if (error) {
    logger.error('Error fetching projects:', error);
    return [];
  }
  
  return (data ?? []) as Project[];
}

/** Get single project by ID */
export async function getProject(id: string): Promise<Project | null> {
  if (isDevCloudActive()) return devCloud.getProject(id); // DEV-ONLY
  if (!supabase) return null;
  
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .eq('id', id)
    .single();
  
  if (error) {
    logger.error('Error fetching project:', error);
    return null;
  }
  
  return data as Project;
}

/** Create new project */
export async function createProject(project: ProjectInsert): Promise<Project | null> {
  if (isDevCloudActive()) return devCloud.createProject(project); // DEV-ONLY
  if (!supabase) return null;
  
  // Ensure user profile exists (fixes FK constraint errors)
  const currentUser = await getCurrentUser();
  if (!currentUser) throw new Error('Not authenticated or profile missing');
  
  const { data, error } = await supabase
    .from('projects')
    .insert({ ...project, user_id: currentUser.id })
    .select()
    .single();
  
  if (error) {
    logger.error('Error creating project:', error);
    throw error;
  }
  
  return data as Project;
}

/** Update existing project */
export async function updateProject(id: string, updates: ProjectUpdate): Promise<Project | null> {
  if (isDevCloudActive()) return devCloud.updateProject(id, updates); // DEV-ONLY
  if (!supabase) return null;
  
  const { data, error } = await supabase
    .from('projects')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) {
    logger.error('Error updating project:', error);
    throw error;
  }
  
  return data as Project;
}

/** Delete project */
export async function deleteProject(id: string): Promise<boolean> {
  if (isDevCloudActive()) return devCloud.deleteProject(id); // DEV-ONLY
  if (!supabase) return false;
  
  const { error } = await supabase
    .from('projects')
    .delete()
    .eq('id', id);
  
  if (error) {
    logger.error('Error deleting project:', error);
    return false;
  }
  
  return true;
}

// =====================================================
// FOLDERS API
// =====================================================

/** Get all folders for current user */
export async function getFolders(): Promise<ProjectFolder[]> {
  if (isDevCloudActive()) return devCloud.getFolders(); // DEV-ONLY
  if (!supabase) return [];
  
  const { data, error } = await supabase
    .from('project_folders')
    .select('*')
    .order('position', { ascending: true });
  
  if (error) {
    logger.error('Error fetching folders:', error);
    return [];
  }
  
  return (data ?? []) as ProjectFolder[];
}

/** Get single folder by ID */
export async function getFolder(id: string): Promise<ProjectFolder | null> {
  if (isDevCloudActive()) return devCloud.getFolder(id); // DEV-ONLY
  if (!supabase) return null;
  
  const { data, error } = await supabase
    .from('project_folders')
    .select('*')
    .eq('id', id)
    .single();
  
  if (error) {
    logger.error('Error fetching folder:', error);
    return null;
  }
  
  return data as ProjectFolder;
}

/** Create new folder */
export async function createFolder(folder: ProjectFolderInsert): Promise<ProjectFolder | null> {
  if (isDevCloudActive()) return devCloud.createFolder(folder); // DEV-ONLY
  if (!supabase) return null;
  
  const currentUser = await getCurrentUser();
  if (!currentUser) throw new Error('Not authenticated');
  
  const { data, error } = await supabase
    .from('project_folders')
    .insert({ ...folder, user_id: currentUser.id })
    .select()
    .single();
  
  if (error) {
    logger.error('Error creating folder:', error);
    throw error;
  }
  
  return data as ProjectFolder;
}

/** Update existing folder */
export async function updateFolder(id: string, updates: ProjectFolderUpdate): Promise<ProjectFolder | null> {
  if (isDevCloudActive()) return devCloud.updateFolder(id, updates); // DEV-ONLY
  if (!supabase) return null;
  
  const { data, error } = await supabase
    .from('project_folders')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) {
    logger.error('Error updating folder:', error);
    throw error;
  }
  
  return data as ProjectFolder;
}

/** Delete folder */
export async function deleteFolder(id: string): Promise<boolean> {
  if (isDevCloudActive()) return devCloud.deleteFolder(id); // DEV-ONLY
  if (!supabase) return false;
  
  const { error } = await supabase
    .from('project_folders')
    .delete()
    .eq('id', id);
  
  if (error) {
    logger.error('Error deleting folder:', error);
    return false;
  }
  
  return true;
}

/** Move project to folder */
export async function moveProjectToFolder(projectId: string, folderId: string | null): Promise<boolean> {
  if (isDevCloudActive()) return devCloud.moveProjectToFolder(projectId, folderId); // DEV-ONLY
  if (!supabase) return false;
  
  const { error } = await supabase
    .from('projects')
    .update({ folder_id: folderId })
    .eq('id', projectId);
  
  if (error) {
    logger.error('Error moving project:', error);
    return false;
  }
  
  return true;
}

/** Toggle project favorite status */
export async function toggleProjectFavorite(projectId: string, isFavorite: boolean): Promise<boolean> {
  if (isDevCloudActive()) return devCloud.toggleProjectFavorite(projectId, isFavorite); // DEV-ONLY
  if (!supabase) return false;
  
  const { error } = await supabase
    .from('projects')
    .update({ is_favorite: isFavorite })
    .eq('id', projectId);
  
  if (error) {
    logger.error('Error toggling favorite:', error);
    return false;
  }
  
  return true;
}

/** Update project tags */
export async function updateProjectTags(projectId: string, tags: string[]): Promise<boolean> {
  if (isDevCloudActive()) return devCloud.updateProjectTags(projectId, tags); // DEV-ONLY
  if (!supabase) return false;
  
  const { error } = await supabase
    .from('projects')
    .update({ tags })
    .eq('id', projectId);
  
  if (error) {
    logger.error('Error updating tags:', error);
    return false;
  }
  
  return true;
}

/** Toggle project pinned status */
export async function toggleProjectPinned(projectId: string, isPinned: boolean): Promise<boolean> {
  if (isDevCloudActive()) return devCloud.toggleProjectPinned(projectId, isPinned); // DEV-ONLY
  if (!supabase) return false;
  
  const { error } = await supabase
    .from('projects')
    .update({ is_pinned: isPinned })
    .eq('id', projectId);
  
  if (error) {
    logger.error('Error toggling pinned:', error);
    return false;
  }
  
  return true;
}

/** Toggle folder pinned status */
export async function toggleFolderPinned(folderId: string, isPinned: boolean): Promise<boolean> {
  if (isDevCloudActive()) return devCloud.toggleFolderPinned(folderId, isPinned); // DEV-ONLY
  if (!supabase) return false;
  
  const { error } = await supabase
    .from('project_folders')
    .update({ is_pinned: isPinned })
    .eq('id', folderId);
  
  if (error) {
    logger.error('Error toggling folder pinned:', error);
    return false;
  }
  
  return true;
}

/** Rename project */
export async function renameProject(projectId: string, name: string): Promise<boolean> {
  if (isDevCloudActive()) return devCloud.renameProject(projectId, name); // DEV-ONLY
  if (!supabase) return false;
  
  const { error } = await supabase
    .from('projects')
    .update({ name })
    .eq('id', projectId);
  
  if (error) {
    logger.error('Error renaming project:', error);
    return false;
  }
  
  return true;
}

/** Update folder parent and position (for drag & drop reorder/reparent) */
export async function updateFolderPosition(
  folderId: string,
  parentId: string | null,
  position: number
): Promise<boolean> {
  if (isDevCloudActive()) return devCloud.updateFolderPosition(folderId, parentId, position); // DEV-ONLY
  if (!supabase) return false;

  const { error } = await supabase
    .from('project_folders')
    .update({ parent_id: parentId, position })
    .eq('id', folderId);

  if (error) {
    logger.error('Error updating folder position:', error);
    return false;
  }

  return true;
}

/** Rename folder */
export async function renameFolder(folderId: string, name: string): Promise<boolean> {
  if (isDevCloudActive()) return devCloud.renameFolder(folderId, name); // DEV-ONLY
  if (!supabase) return false;
  
  const { error } = await supabase
    .from('project_folders')
    .update({ name })
    .eq('id', folderId);
  
  if (error) {
    logger.error('Error renaming folder:', error);
    return false;
  }
  
  return true;
}

// =====================================================
// TEMPLATES API
// =====================================================

export interface Template {
  id: string;
  name: string;
  description: string | null;
  category: 'attack' | 'defense' | 'set-piece' | 'training' | 'formation' | 'other' | null;
  document: BoardDocument;
  thumbnail_url: string | null;
  author_id: string | null;
  author_name: string | null;
  downloads_count: number;
  is_featured: boolean;
  created_at: string;
}

/** Get all templates */
export async function getTemplates(category?: string): Promise<Template[]> {
  if (!supabase) return [];
  
  let query = supabase
    .from('templates')
    .select('*')
    .order('downloads_count', { ascending: false });
  
  if (category) {
    query = query.eq('category', category);
  }
  
  const { data, error } = await query;
  
  if (error) {
    logger.error('Error fetching templates:', error);
    return [];
  }
  
  return (data ?? []) as Template[];
}

/** Get featured templates */
export async function getFeaturedTemplates(): Promise<Template[]> {
  if (!supabase) return [];
  
  const { data, error } = await supabase
    .from('templates')
    .select('*')
    .eq('is_featured', true)
    .order('downloads_count', { ascending: false })
    .limit(6);
  
  if (error) {
    logger.error('Error fetching featured templates:', error);
    return [];
  }
  
  return (data ?? []) as Template[];
}

// =====================================================
// STORAGE HELPERS
// =====================================================

/** Upload project thumbnail */
export async function uploadThumbnail(projectId: string, file: Blob): Promise<string | null> {
  if (isDevCloudActive()) return devCloud.uploadThumbnail(projectId, file); // DEV-ONLY: mock data-URL
  if (!supabase) return null;
  
  const fileName = `${projectId}/thumbnail.png`;
  
  const { error } = await supabase.storage
    .from('thumbnails')
    .upload(fileName, file, { upsert: true });
  
  if (error) {
    logger.error('Error uploading thumbnail:', error);
    return null;
  }
  
  const { data } = supabase.storage
    .from('thumbnails')
    .getPublicUrl(fileName);
  
  return data.publicUrl;
}

/** Upload user avatar */
export async function uploadAvatar(userId: string, file: File): Promise<string | null> {
  if (!supabase) return null;
  
  const fileExt = file.name.split('.').pop();
  const fileName = `${userId}/avatar.${fileExt}`;
  
  const { error } = await supabase.storage
    .from('avatars')
    .upload(fileName, file, { upsert: true });
  
  if (error) {
    logger.error('Error uploading avatar:', error);
    return null;
  }
  
  const { data } = supabase.storage
    .from('avatars')
    .getPublicUrl(fileName);
  
  return data.publicUrl;
}

// =====================================================
// AUTH STATE LISTENER
// =====================================================

/** Subscribe to auth state changes */
export function onAuthStateChange(callback: (user: User | null) => void) {
  if (!supabase) {
    callback(null);
    return () => {};
  }
  
  const { data: { subscription } } = supabase.auth.onAuthStateChange(
    (_event, session) => {
      if (!session?.user) {
        callback(null);
        return;
      }

      // Supabase awaits async auth-state callbacks during PKCE exchange. Do not
      // make the exchange wait for the profile query; hydrate it in the background.
      void (async () => {
        try {
          // Pass the session user so we don't make a redundant getUser() call.
          const user = await getCurrentUser(session.user);
          callback(user);
        } catch (err) {
          // Never leave the UI logged-out just because the profile fetch failed
          // (network blip / AbortError during PKCE exchange). Fall back to the
          // basic identity from the session so the app reflects the login now;
          // the real subscription tier is refreshed on the next auth event/load.
          logger.error('[Auth] onAuthStateChange handler failed - using session fallback:', err);
          callback({
            id: session.user.id,
            email: session.user.email ?? '',
            full_name:
              session.user.user_metadata?.full_name ??
              session.user.user_metadata?.name ??
              undefined,
            avatar_url:
              session.user.user_metadata?.avatar_url ??
              session.user.user_metadata?.picture ??
              undefined,
            subscription_tier: 'free',
          });
        }
      })();
    }
  );
  
  return () => subscription.unsubscribe();
}
